import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getAlternatePath } from '../config/routes';
import { Mail, Phone, MapPin, Facebook, Instagram } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_arce-escuela/artifacts/yy55ojqv_arcelogo.jpeg';

function lp(lang, path) { return `/${lang}${path}`; }

export default function Footer() {
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  const switchLang = () => {
    const target = language === 'es' ? 'en' : 'es';
    const newPath = getAlternatePath(location.pathname, target);
    toggleLanguage(target);
    navigate(newPath);
  };

  const l = (esSlug, enSlug) => lp(language, language === 'es' ? esSlug : enSlug);

  return (
    <footer className="bg-[#1D1D1F] text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo */}
          <div className="lg:col-span-1">
            <Link to={l('/', '/')} className="inline-block mb-6" data-testid="footer-logo">
              <img src={LOGO_URL} alt="Colegio ARCE" loading="lazy" className="h-20 w-auto" />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed">
              {language === 'es' 
                ? 'Clases virtuales de español para niños y jóvenes. Arraigo y Cultura Española.' 
                : 'Virtual Spanish classes for children and teens. Spanish Roots and Culture.'}
            </p>
            <div className="flex items-center gap-4 mt-6">
              <a href="https://www.facebook.com/colegioarce" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#E63946] transition-colors" data-testid="social-facebook">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/colegioarce" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#E63946] transition-colors" data-testid="social-instagram">
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Escolaridad */}
          <div>
            <h4 className="font-semibold text-lg mb-6">{t('nav.escolaridad')}</h4>
            <ul className="space-y-3">
              <li><Link to={l('/programas', '/programs')} className="text-gray-400 hover:text-white transition-colors" data-testid="footer-courses">{t('nav.coursesLevels')}</Link></li>
              <li><Link to={l('/metodologia', '/methodology')} className="text-gray-400 hover:text-white transition-colors" data-testid="footer-methodology">{t('nav.methodology')}</Link></li>
              <li><Link to={l('/calendario', '/calendar')} className="text-gray-400 hover:text-white transition-colors" data-testid="footer-calendar">{language === 'es' ? 'Calendario' : 'Calendar'}</Link></li>
              <li><Link to={l('/tarifas', '/pricing')} className="text-gray-400 hover:text-white transition-colors" data-testid="footer-pricing">{t('nav.pricing')}</Link></li>
              <li><Link to={`/login?redirect=${l('/inscripciones', '/enrollment')}`} className="text-gray-400 hover:text-white transition-colors" data-testid="footer-enrollment">{t('nav.register')}</Link></li>
            </ul>
          </div>

          {/* Sobre Nosotros */}
          <div>
            <h4 className="font-semibold text-lg mb-6">{t('nav.aboutUs')}</h4>
            <ul className="space-y-3">
              <li><Link to={l('/quienes-somos', '/about')} className="text-gray-400 hover:text-white transition-colors" data-testid="footer-about">{t('nav.whoWeAre')}</Link></li>
              <li><Link to={l('/filosofia', '/philosophy')} className="text-gray-400 hover:text-white transition-colors" data-testid="footer-philosophy">{language === 'es' ? 'Filosofía' : 'Philosophy'}</Link></li>
              <li><Link to={l('/equipo', '/team')} className="text-gray-400 hover:text-white transition-colors" data-testid="footer-team">{t('nav.team')}</Link></li>
              <li><Link to={l('/blog', '/blog')} className="text-gray-400 hover:text-white transition-colors" data-testid="footer-blog">{t('nav.blog')}</Link></li>
              <li><Link to={l('/contacto', '/contact')} className="text-gray-400 hover:text-white transition-colors" data-testid="footer-contact">{t('nav.contact')}</Link></li>
              <li><Link to={l('/preguntas-frecuentes', '/faq')} className="text-gray-400 hover:text-white transition-colors" data-testid="footer-faq">{language === 'es' ? 'Preguntas Frecuentes' : 'FAQ'}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-6">{t('nav.contact')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#E63946] flex-shrink-0 mt-0.5" />
                <span className="text-gray-400">Medford, MA 02155<br />United States</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-[#E63946] flex-shrink-0" />
                <a href="mailto:laura@colegioarce.com" className="text-gray-400 hover:text-white transition-colors">
                  laura@colegioarce.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-[#E63946] flex-shrink-0" />
                <a href="https://wa.me/13392277556" className="text-gray-400 hover:text-white transition-colors">
                  WhatsApp
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} Colegio ARCE. {t('footer.rights')}.
            </p>
            <div className="flex items-center gap-6">
              <Link to={l('/privacidad', '/privacy')} className="text-gray-500 hover:text-gray-300 text-sm transition-colors" data-testid="footer-privacy">
                {t('footer.privacy')}
              </Link>
              <Link to={l('/terminos', '/terms')} className="text-gray-500 hover:text-gray-300 text-sm transition-colors" data-testid="footer-terms">
                {t('footer.terms')}
              </Link>
              <div className="lang-toggle bg-gray-800" data-testid="footer-language-toggle">
                <button onClick={switchLang} className={language === 'es' ? 'active !bg-gray-700' : ''}>ES</button>
                <button onClick={switchLang} className={language === 'en' ? 'active !bg-gray-700' : ''}>EN</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
